import { formatNumber } from '@dealstack/shared/lib/format/number';
import { RecordAny } from '@dealstack/shared/lib/common/common';
import { HYPHEN_MINUS } from '@dealstack/frontend/shared/other/react/specialSigns';
import { Text, TextInput } from '@mantine/core';
import React from 'react';
import {
	InfoDiv,
	TotalDetailsDiv,
	UniqueDiv,
} from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import { createSortableTextHeader } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnHeader/ColumnHeader';

type Props<T extends RecordAny> = ExtendedColumnDef<T> & {
	currency?: string;
	lockPrecision?: boolean;
	headerText?: string;
	filterText?: string;
	minPrecision?: number;
	maxPrecision?: number;
	footerLockPrecision?: boolean;
	footerMinPrecision?: number;
	footerMaxPrecision?: number;
	showFooter?: boolean;
	footerValue?: number;
	text?: string;
	enableSorting?: boolean;
};

export const createNumberColumn = <T extends RecordAny>({
	lockPrecision,
	minPrecision,
	maxPrecision,
	footerLockPrecision,
	footerMinPrecision,
	footerMaxPrecision,
	showFooter = false,
	footerValue,
	text,
	headerText,
	enableSorting = true,
	...columnDef
}: Props<T>): ExtendedColumnDef<T> => {
	return {
		header: headerText
			? createSortableTextHeader(
					headerText,
					enableSorting,
					{
						style: {
							width: '100%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'flex-end',
							textAlign: 'right',
						},
					},
					'right'
				)
			: columnDef.header,
		columnLabel: columnDef.columnLabel ?? headerText ?? '',
		cell: ({ cell }) => {
			const cellValue = cell.getValue();

			let formattedValue = '';
			if (cellValue == null) {
				formattedValue = HYPHEN_MINUS;
			} else if (typeof cellValue === 'number') {
				formattedValue = formatNumber({
					value: cellValue,
					lockPrecision,
					minPrecision,
					maxPrecision,
				});
			} else if (typeof cellValue === 'string') {
				formattedValue = cellValue;
			} else {
				const currency = (cellValue as Record<string, unknown>)?.currency;
				const value = (cellValue as Record<string, unknown>)?.value;

				formattedValue = formatNumber({
					value: value as number,
					currency: currency as string,
					lockPrecision,
					minPrecision,
					maxPrecision,
				});
			}

			return (
				<div
					style={{
						width: '100%',
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'flex-end',
					}}
				>
					<Text size="B6Medium">{formattedValue}</Text>
				</div>
			);
		},
		footer: showFooter
			? () => {
					let formattedFooterValue = '';
					if (footerValue != null) {
						formattedFooterValue = formatNumber({
							value: footerValue,
							lockPrecision: footerLockPrecision ?? lockPrecision,
							minPrecision: footerMinPrecision ?? minPrecision,
							maxPrecision: footerMaxPrecision ?? maxPrecision,
						});
					} else {
						formattedFooterValue = HYPHEN_MINUS;
					}

					return (
						<TotalDetailsDiv>
							<UniqueDiv>{text}</UniqueDiv>
							<InfoDiv>{formattedFooterValue}</InfoDiv>
						</TotalDetailsDiv>
					);
				}
			: undefined,
		enableSorting,
		filterValueType: 'number',
		filter: columnDef.showFilter
			? ({ column }) => (
					<TextInput
						value={column.getFilterValue() as string}
						onChange={(e) => column.setFilterValue(e.currentTarget.value)}
						placeholder={columnDef.filterText}
					/>
				)
			: undefined,
		...columnDef,
	};
};
